/**
 * Local device/operator configuration, split into two layers that are
 * configured (and cleared) independently:
 *
 *  - Device config: the Worker URL and the stopgap X-App-Token shared
 *    secret every /api/* route is gated behind (apps/worker/src/index.ts's
 *    "Stopgap shared-secret gate" middleware). Configured once per
 *    device, shared by whoever uses it — not specific to one operator.
 *
 *  - Operator session: who is currently logged in on THIS device. Set by
 *    a real PIN login (POST /api/auth/login) and sent back as
 *    X-Operator-Token on every subsequent request. Replaces the old
 *    free-text Operator ID field — Suri: "No real login — every device
 *    just has a free-text Operator ID in Settings with no password;
 *    anyone who knows (or guesses) a name can act as that person."
 *    Cleared on logout, or automatically by api.ts when the server
 *    reports the session invalid/expired/revoked.
 *
 * All stored in localStorage only — never sent anywhere but this
 * device's own configured Worker, and never the Mintsoft API key itself,
 * which never leaves the backend (spec §4).
 */
const KEYS = {
  serverUrl: 'wr.serverUrl',
  appToken: 'wr.appToken',
  operatorToken: 'wr.operatorToken',
  operatorId: 'wr.operatorId',
  operatorName: 'wr.operatorName',
  operatorRole: 'wr.operatorRole',
} as const;

export interface DeviceConfig {
  serverUrl: string;
  appToken: string;
}

export function loadConfig(): DeviceConfig {
  return {
    serverUrl: localStorage.getItem(KEYS.serverUrl) ?? '',
    appToken: localStorage.getItem(KEYS.appToken) ?? '',
  };
}

export function saveConfig(config: DeviceConfig): void {
  localStorage.setItem(KEYS.serverUrl, config.serverUrl.trim().replace(/\/$/, ''));
  localStorage.setItem(KEYS.appToken, config.appToken.trim());
}

export function isConfigured(config: DeviceConfig): boolean {
  return config.serverUrl !== '' && config.appToken !== '';
}

/** Clears the device config AND the operator session — used when the device itself is being reset/reassigned, not just when one operator logs out. */
export function clearConfig(): void {
  localStorage.removeItem(KEYS.serverUrl);
  localStorage.removeItem(KEYS.appToken);
  clearOperatorSession();
}

export interface OperatorSession {
  token: string;
  id: string;
  name: string;
  role: 'OPERATOR' | 'SUPERVISOR' | 'ADMIN';
}

export function loadOperatorSession(): OperatorSession | null {
  const token = localStorage.getItem(KEYS.operatorToken);
  const id = localStorage.getItem(KEYS.operatorId);
  const name = localStorage.getItem(KEYS.operatorName);
  const role = localStorage.getItem(KEYS.operatorRole);
  if (!token || !id || !name || !role) return null;
  return { token, id, name, role: role as OperatorSession['role'] };
}

export function saveOperatorSession(session: OperatorSession): void {
  localStorage.setItem(KEYS.operatorToken, session.token);
  localStorage.setItem(KEYS.operatorId, session.id);
  localStorage.setItem(KEYS.operatorName, session.name);
  localStorage.setItem(KEYS.operatorRole, session.role);
}

export function clearOperatorSession(): void {
  localStorage.removeItem(KEYS.operatorToken);
  localStorage.removeItem(KEYS.operatorId);
  localStorage.removeItem(KEYS.operatorName);
  localStorage.removeItem(KEYS.operatorRole);
}
